import { useState, useMemo, useCallback, useEffect } from "react";
import Select from "components/ui/Select";
import { useAllShapes } from "http-hooks/shape/useShape";
import { useAllColors } from "http-hooks/color/useColor";
import { useAllSizes } from "http-hooks/size/useSize";

const sizeTypeOptions = [
  { value: "B", label: "Big" },
  { value: "S", label: "Small" },
];

/**
 * InlineCellSelect — wraps our app Select component inside a table cell.
 * Commits on single selection, cancels on Escape / outside click (handled by Select itself).
 */
function InlineCellSelect({ options, currentValue, onCommit, onCancel, isLoading }) {
  const selected = options.find((o) => String(o.value) === String(currentValue)) ?? null;

  const handleChange = (picked) => {
    if (picked) onCommit(picked);
    else onCancel();
  };

  return (
    <div
      className="min-w-[130px]"
      // Stop double-click bubbling so opening doesn't immediately re-trigger
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <Select
        value={selected}
        onChange={handleChange}
        options={options}
        isLoading={isLoading}
        showLabel={false}
        isClearable={false}
        isSearchable={true}
        placeholder="Search..."
        openOnMount={true}
      />
    </div>
  );
}

/**
 * CombinationsTable
 *
 * Props:
 *   shapes        — array of selected shape objects
 *   colors        — array of selected color objects
 *   sizes         — array of selected size objects
 *   sizeType      — current sizeType ("B" | "S")
 *   unitName      — unit name string (for size filter)
 *   isShapeEnabled, isColorEnabled, isSizeEnabled — booleans from company prefs
 *   onChange      — (selectedCombinations: array) => void  called whenever selection changes
 */
export default function CombinationsTable({
  shapes,
  colors,
  sizes,
  sizeType,
  unitName,
  isShapeEnabled,
  isColorEnabled,
  isSizeEnabled,
  onChange,
}) {
  const [editingCell, setEditingCell] = useState(null);
  const [rows, setRows] = useState([]);

  // Load all available options for inline editing dropdowns
  const { data: { data: allShapes } = {}, isLoading: loadingShapes } = useAllShapes({
    ag_status: "",
    noPagination: true,
  });
  const { data: { data: allColors } = {}, isLoading: loadingColors } = useAllColors({
    ag_status: "",
    noPagination: true,
  });
  const { data: { data: allSizes } = {}, isLoading: loadingSizes } = useAllSizes({
    ag_status: "1",
    noPagination: true,
    ...(unitName ? { umid: unitName } : {}),
  });

  const shapeOptions = useMemo(
    () =>
      (allShapes || []).map((s) => ({
        value: s.shapeId,
        label: s.shapetype,
        shapetype: s.shapetype,
        shapeId: s.shapeId,
      })),
    [allShapes]
  );
  const colorOptions = useMemo(
    () =>
      (allColors || []).map((c) => ({
        value: c.clrId,
        label: c.clrname,
        clrname: c.clrname,
        clrId: c.clrId,
      })),
    [allColors]
  );
  const sizeOptions = useMemo(
    () =>
      (allSizes || []).map((s) => ({
        value: s.itmszid,
        label: s.itmsz,
        itmsz: s.itmsz,
        itmszid: s.itmszid,
      })),
    [allSizes]
  );

  // Recompute Cartesian product whenever inputs change.
  // Only produce rows when at least one enabled attribute has actual selections.
  useEffect(() => {
    const hasShapes = isShapeEnabled && shapes.length > 0;
    const hasColors = isColorEnabled && colors.length > 0;
    const hasSizes = isSizeEnabled && sizes.length > 0;

    // Nothing selected yet — show empty state
    if (!hasShapes && !hasColors && !hasSizes) {
      setRows([]);
      setEditingCell(null);
      return;
    }

    const shapeList = hasShapes ? shapes : [{ shapeId: 0, shapetype: "", value: 0, label: "" }];
    const colorList = hasColors ? colors : [{ clrId: 0, clrname: "", value: 0, label: "" }];
    const sizeList  = hasSizes  ? sizes  : [{ itmszid: 0, itmsz: "",   value: 0, label: "" }];

    const generated = [];
    shapeList.forEach((shape) => {
      colorList.forEach((color) => {
        sizeList.forEach((size) => {
          generated.push({
            _id: `${shape.shapeId ?? shape.value}-${color.clrId ?? color.value}-${size.itmszid ?? size.value}`,
            shape: {
              shapeId:   shape.shapeId   ?? shape.value  ?? 0,
              shapetype: shape.shapetype ?? shape.label  ?? "",
              value:     shape.value     ?? shape.shapeId ?? 0,
              label:     shape.label     ?? shape.shapetype ?? "",
            },
            color: {
              clrId:   color.clrId   ?? color.value  ?? 0,
              clrname: color.clrname ?? color.label  ?? "",
              value:   color.value   ?? color.clrId  ?? 0,
              label:   color.label   ?? color.clrname ?? "",
            },
            size: {
              itmszid: size.itmszid ?? size.value  ?? 0,
              itmsz:   size.itmsz  ?? size.label   ?? "",
              value:   size.value  ?? size.itmszid ?? 0,
              label:   size.label  ?? size.itmsz   ?? "",
            },
            sizeType: sizeType || "B",
            checked: true,
          });
        });
      });
    });

    setRows(generated);
    setEditingCell(null);
  }, [shapes, colors, sizes, sizeType, isShapeEnabled, isColorEnabled, isSizeEnabled]);

  // Notify parent whenever rows change
  useEffect(() => {
    const selected = rows
      .filter((r) => r.checked)
      .map(({ shape, color, size, sizeType: st }) => ({ shape, color, size, sizeType: st }));
    onChange(selected);
  }, [rows, onChange]);

  const selectedCount = useMemo(() => rows.filter((r) => r.checked).length, [rows]);
  const allChecked  = rows.length > 0 && selectedCount === rows.length;
  const someChecked = selectedCount > 0 && selectedCount < rows.length;

  const toggleAll = useCallback(() => {
    setRows((prev) => prev.map((r) => ({ ...r, checked: !allChecked })));
  }, [allChecked]);

  const toggleRow = useCallback((id) => {
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, checked: !r.checked } : r))
    );
  }, []);

  const handleCellDoubleClick = useCallback((rowId, field) => {
    setEditingCell({ rowId, field });
  }, []);

  const commitEdit = useCallback((rowId, field, picked) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r;
        if (field === "shape") {
          return {
            ...r,
            shape: { shapeId: picked.value, shapetype: picked.label, value: picked.value, label: picked.label },
          };
        }
        if (field === "color") {
          return {
            ...r,
            color: { clrId: picked.value, clrname: picked.label, value: picked.value, label: picked.label },
          };
        }
        if (field === "size") {
          return {
            ...r,
            size: { itmszid: picked.value, itmsz: picked.label, value: picked.value, label: picked.label },
          };
        }
        if (field === "sizeType") {
          return { ...r, sizeType: picked.value };
        }
        return r;
      })
    );
    setEditingCell(null);
  }, []);

  const cancelEdit = useCallback(() => setEditingCell(null), []);

  // Nothing to show until attributes are chosen
  if (rows.length === 0) return null;

  const showShape = isShapeEnabled;
  const showColor = isColorEnabled;
  const showSize  = isSizeEnabled;

  return (
    <div className="mt-6">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Combinations
          </h3>
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
            {selectedCount}/{rows.length} selected
          </span>
        </div>
        <p className="text-xs text-gray-400 italic">
          Double-click any cell to change its value
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                #
              </th>
              {showShape && (
                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                  Shape
                </th>
              )}
              {showColor && (
                <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                  Color
                </th>
              )}
              {showSize && (
                <>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Size
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Size Type
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((row, idx) => {
              const isEditing = (field) =>
                editingCell?.rowId === row._id && editingCell?.field === field;

              return (
                <tr
                  key={row._id}
                  className={`transition-colors ${
                    row.checked ? "bg-white hover:bg-gray-50" : "bg-gray-50 opacity-50"
                  }`}
                >
                  {/* Checkbox */}
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={() => toggleRow(row._id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                  </td>

                  {/* Row number */}
                  <td className="px-3 py-2 text-gray-400 text-xs tabular-nums">
                    {idx + 1}
                  </td>

                  {/* Shape */}
                  {showShape && (
                    <td
                      className="px-3 py-1.5 min-w-[140px] cursor-pointer group relative"
                      onDoubleClick={() => handleCellDoubleClick(row._id, "shape")}
                      title="Double-click to edit"
                    >
                      {isEditing("shape") ? (
                        <InlineCellSelect
                          options={shapeOptions}
                          currentValue={row.shape.value}
                          isLoading={loadingShapes}
                          onCommit={(picked) => commitEdit(row._id, "shape", picked)}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        <CellDisplay value={row.shape.shapetype} />
                      )}
                    </td>
                  )}

                  {/* Color */}
                  {showColor && (
                    <td
                      className="px-3 py-1.5 min-w-[140px] cursor-pointer group relative"
                      onDoubleClick={() => handleCellDoubleClick(row._id, "color")}
                      title="Double-click to edit"
                    >
                      {isEditing("color") ? (
                        <InlineCellSelect
                          options={colorOptions}
                          currentValue={row.color.value}
                          isLoading={loadingColors}
                          onCommit={(picked) => commitEdit(row._id, "color", picked)}
                          onCancel={cancelEdit}
                        />
                      ) : (
                        <CellDisplay value={row.color.clrname} />
                      )}
                    </td>
                  )}

                  {/* Size */}
                  {showSize && (
                    <>
                      <td
                        className="px-3 py-1.5 min-w-[120px] cursor-pointer group relative"
                        onDoubleClick={() => handleCellDoubleClick(row._id, "size")}
                        title="Double-click to edit"
                      >
                        {isEditing("size") ? (
                          <InlineCellSelect
                            options={sizeOptions}
                            currentValue={row.size.value}
                            isLoading={loadingSizes}
                            onCommit={(picked) => commitEdit(row._id, "size", picked)}
                            onCancel={cancelEdit}
                          />
                        ) : (
                          <CellDisplay value={row.size.itmsz} />
                        )}
                      </td>

                      <td
                        className="px-3 py-1.5 min-w-[110px] cursor-pointer group relative"
                        onDoubleClick={() => handleCellDoubleClick(row._id, "sizeType")}
                        title="Double-click to edit"
                      >
                        {isEditing("sizeType") ? (
                          <InlineCellSelect
                            options={sizeTypeOptions}
                            currentValue={row.sizeType}
                            onCommit={(picked) => commitEdit(row._id, "sizeType", picked)}
                            onCancel={cancelEdit}
                          />
                        ) : (
                          <CellDisplay value={row.sizeType === "B" ? "Big" : "Small"} />
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Cell display with a subtle pencil hint on hover
function CellDisplay({ value }) {
  return (
    <span className="flex items-center gap-1">
      <span className="text-gray-800">{value || "—"}</span>
      <PencilHint />
    </span>
  );
}

function PencilHint() {
  return (
    <svg
      className="h-3 w-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 3.487a2.25 2.25 0 013.182 3.182L7.5 19.212l-4.5 1.125 1.125-4.5L16.862 3.487z"
      />
    </svg>
  );
}
